import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Bot, Send, Sparkles, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { chatText } from "../lib/openai";

const SYSTEM_PROMPT = `You are BuddyPass AI Coach — an in-app assistant specialized in the business model of using AI to create and sell digital products online, and in helping creators improve their scripts, offers, and navigate any tab in BuddyPass AI.

Your expertise (stay strictly within this scope):
- Picking profitable digital-product niches and validating demand
- Designing offers with AI (ebooks, templates, toolkits, notion packs, prompt packs, mini-courses, swipe files)
- Pricing strategy and offer ladders (free lead magnet → low-ticket → premium → high-ticket)
- Driving traffic with short-form content (TikTok, Reels, YouTube Shorts)
- Hooks, scripts, CTAs, captions, hashtags, AI-video prompts
- Affiliate marketing — especially The Nomadic Wealth Academy partnership
- Building an audience as a digital nomad / creator
- How to use BuddyPass AI™ (the 3 tabs: Partnership Portal, Create Your Asset, Create Your Content)

HARD RULES FOR SCRIPT IMPROVEMENTS (do NOT violate — these keep the 4-scene Viral Reel generator working):
When a user asks you to tweak, review, or rewrite any script they generated in BuddyPass:
- Preserve the EXACT 4-scene structure: HOOK → PROBLEM → INSIGHT → CTA. Never add a 5th scene, never collapse to 3.
- HOOK / PROBLEM / INSIGHT scenes are ONE short sentence each, 8–12 words. CTA scene is 12–18 words.
- Total video stays 12 seconds (3 seconds per scene). Fast pacing only.
- Each scene has a spoken \`script\` AND an \`on_screen_text\` field. The on_screen_text MUST equal the script for that scene EXACTLY (verbatim, no paraphrasing).
- Keep the CTA format strictly: "If you type KEYWORD, I'll send breakdown so you can [OUTCOME]." — KEYWORD is one uppercase word, the literal word "breakdown" is fixed (it's a spoken follow-up, NOT a deliverable).
- The CTA must NEVER promise a spreadsheet, pdf, ebook, guide, blueprint, playbook, framework, course, training, masterclass, workshop, checklist, cheat-sheet, template, link, file, freebie, bonus, or any download. Only a spoken breakdown.
- [OUTCOME] is a concrete opportunity-framed end result (e.g. "live cheaper abroad", "build remote income", "make money while traveling"). No timeframes. No problem-avoidance ("so you can stop X").
- Image prompts are PURELY VISUAL — describe a real cinematic photograph. Never include text, words, captions, signage, logos, watermarks, screen UI, app interfaces, or any AI-generated text artifacts.
- Never introduce marketing / ad language: "transform your life", "unlock", "discover", "explore", "imagine", "ready to", "this lifestyle", "change your life", "game-changer", "level up", "empower", "take control".
- Never introduce voiceover cues, stage directions, or POV forcing — these are TEXT-ONLY faceless reels.
- Never merge any separate UGC-avatar script (HeyGen/ElevenLabs mode) back into the 4-scene reel — those are two different output modes.
- If the user's requested change would break any of the above, gently push back and offer a version that stays inside the rules.

Rules:
- Be direct, specific, actionable. No fluff. No "in today's world" clichés.
- Keep answers concise (≤ ~180 words) unless the user asks for more depth.
- When the user's question is off-topic, politely redirect to digital-product / content / nomad-business territory.
- Use short headings or bullets only when they add clarity. Otherwise write in plain prose.
- When appropriate, suggest which BuddyPass tab the user should go to next.`;

export const AskAIDialog = ({ apiKey }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey — I'm your BuddyPass Coach. I can help you improve your scripts, offers or help you navigate any tab in this tool. What are we solving today?",
    },
  ]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setLoading(true);
    try {
      // Build a condensed transcript for context (last 8 turns)
      const history = next
        .slice(-8)
        .map((m) => `${m.role === "user" ? "User" : "Coach"}: ${m.content}`)
        .join("\n\n");
      const reply = await chatText({
        apiKey,
        system: SYSTEM_PROMPT,
        user: history + "\n\nCoach:",
        temperature: 0.6,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: reply.trim() || "(no response)" },
      ]);
    } catch (e) {
      toast.error(e.message || "Ask AI failed");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `⚠️ ${e.message || "Something went wrong."}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const quickPrompts = [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="ask-ai-button"
          className="rounded-full bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200 border border-amber-500/30 text-xs h-9 px-3.5"
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} />
          Ask AI
        </Button>
      </DialogTrigger>

      <DialogContent
        className="sm:max-w-2xl bg-zinc-950 border border-white/10 text-white p-0 overflow-hidden"
        data-testid="ask-ai-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
            <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
              <Bot className="h-4 w-4" strokeWidth={1.8} />
            </div>
            BuddyPass AI Coach
          </DialogTitle>
          <p className="text-xs text-zinc-500 pt-1">
            Specialist in using AI to create & sell digital products. Scoped to this business model.
          </p>
        </DialogHeader>

        <div
          ref={scrollerRef}
          className="px-6 py-5 max-h-[52vh] overflow-y-auto space-y-4"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
            >
              <div
                className={`shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ${
                  m.role === "user"
                    ? "bg-zinc-800 text-zinc-300 ring-white/10"
                    : "bg-amber-500/10 text-amber-400 ring-amber-500/30"
                }`}
              >
                {m.role === "user" ? (
                  <UserIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
                ) : (
                  <Bot className="h-3.5 w-3.5" strokeWidth={1.8} />
                )}
              </div>
              <div
                className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed max-w-[82%] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-amber-500/10 border border-amber-500/30 text-amber-50"
                    : "bg-zinc-900 border border-white/10 text-zinc-200"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30">
                <Bot className="h-3.5 w-3.5" strokeWidth={1.8} />
              </div>
              <div className="rounded-xl px-3.5 py-2.5 bg-zinc-900 border border-white/10 text-zinc-400 inline-flex items-center gap-2 text-sm">
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                thinking…
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && !loading && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {quickPrompts.map((p, i) => (
              <button
                key={i}
                onClick={() => setInput(p)}
                className="text-[11px] rounded-full border border-white/10 bg-zinc-900 px-3 py-1.5 text-zinc-400 hover:text-white hover:border-amber-500/50 transition-all"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-4 border-t border-white/10 bg-zinc-950">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Tell me what you're trying to do—I'll help you build it."
              data-testid="ask-ai-input"
              className="min-h-[44px] max-h-36 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-amber-500 resize-none text-sm"
            />
            <Button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="ask-ai-send"
              className="h-11 rounded-full bg-amber-500 text-[#1c1302] font-semibold hover:bg-amber-400 disabled:opacity-50 px-4"
            >
              <Send className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">
            Enter to send · Shift + Enter for new line · Uses your OpenAI key (gpt-4o-mini)
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AskAIDialog;
